import React, { useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import { userService } from "../../services";
import { Box, CircularProgress, Container } from "@mui/material";
import { TextField } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  Add,
  Clear,
  Delete,
  Edit,
  PersonAddAlt1,
  PersonRemoveAlt1,
  Search,
} from "@mui/icons-material";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import ResponsiveDialogManager from "../../components/dialog/ResponsiveDialogManager";
import UserForm from "../../components/forms/UserForm";
import ToolbarDefaultActions from "../../components/dialog/toolbarDefaultActions";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import ConfirmationDialog from "../../components/dialog/ConfirmationDialog";
import SettingsIcon from "@mui/icons-material/Settings";
import UserRights from "../../components/forms/UserRights";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import {
  updateUserRights,
  updateUserSuperAdmin,
} from "../../reducers/user/userSlice";

const PREFIX = "AdminUsers";

const classes = {
  marginBottom: `${PREFIX}-marginBottom`,
  filter: `${PREFIX}-filter`,
  filterButtons: `${PREFIX}-filterButtons`,
  mobileMarginBottom: `${PREFIX}-mobileMarginBottom`,
};

const StyledDiv = styled("div")(({ theme }) => ({
  [`& .${classes.marginBottom}`]: {
    marginBottom: "0.5rem",
  },

  [`& .${classes.filter}`]: {
    display: "flex",
    justifyContent: "space-around",
  },

  [`& .${classes.filterButtons}`]: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "1rem",
    marginTop: "1rem",
  },

  [`&.${classes.mobileMarginBottom}`]: {
    [theme.breakpoints.between("xs", "md")]: {
      marginBottom: "5rem",
    },
  },
}));

const LinkVipAvatar = ({ params, setUsers, users }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [saving, setSaving] = useState(false);

  const handleRemoveVipAvatar = () => {
    setSaving(true);
    userService
      .deleteAvatarVip(params.id)
      .then(() => {
        enqueueSnackbar(`${t("toasts.userUpdateSuccess")}`, {
          variant: "success",
        });
        setSaving(false);
        const tmp = [...users];
        const myIndex = tmp.findIndex((item) => item.id === params.id);
        const myObj = tmp[myIndex];
        delete myObj.avatarVip;
        tmp[myIndex] = myObj;
        setUsers(tmp);
      })
      .catch((err) => {
        enqueueSnackbar(`${t("toasts.unknownError")}`, {
          variant: "error",
        });
        setSaving(false);
      });
  };

  const handleAddVipAvatar = (file) => {
    userService
      .uploadAvatarVip(params.id, file)
      .then((res) => {
        enqueueSnackbar(`${t("toasts.userUpdateSuccess")}`, {
          variant: "success",
        });
        setSaving(false);
        const tmp = [...users];
        const myIndex = tmp.findIndex((item) => item.id === params.id);
        tmp[myIndex] = { ...tmp[myIndex], avatarVip: res.avatarVip };
        setUsers(tmp);
      })
      .catch((err) => {
        enqueueSnackbar(`${t("toasts.unknownError")}`, {
          variant: "error",
        });
        setSaving(false);
      });
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <input
        type="file"
        accept=".gltf,.glb"
        name="add vip avatar"
        id={`avatar-vip-model-modify-button-${params.id}`}
        style={{ display: "none" }}
        onClick={(e) => (e.target.value = null)} //reinitialize the value to retrigger the onChange on the same file
        onChange={(e) => {
          e.stopPropagation();
          handleAddVipAvatar(e.target.files[0]);
          setSaving(true);
        }}
      />
      <label htmlFor={`avatar-vip-model-modify-button-${params.id}`}>
        <IconButton
          color="primary"
          aria-label="upload picture"
          component="span"
          disabled={saving}
          onChange={(e) => {
            e.stopPropagation();
          }}
        >
          {saving ? (
            <CircularProgress size={28} color="primary" />
          ) : (
            <PersonAddAlt1 />
          )}
        </IconButton>
      </label>
      {params?.avatarVip && (
        <IconButton
          color="primary"
          aria-label="upload picture"
          component="span"
          disabled={saving}
          onClick={handleRemoveVipAvatar}
        >
          {saving ? null : <PersonRemoveAlt1 />}
        </IconButton>
      )}
    </Box>
  );
};

export const AdminUsers = (props) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const userSuperAdmin = useSelector(
    (state) => state.user?.currentUser?.account?.isSuperAdmin
  );
  const myUserId = useSelector((state) => state.user?.currentUser?.account?.id);
  const [users, setUsers] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [page, setPage] = React.useState(1);
  const [rowCount, setRowCount] = React.useState(0);
  const [login, setLogin] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [platform, setPlatform] = React.useState("");
  const [sortModel, setSortModel] = React.useState({
    field: "createdDate",
    sort: "desc",
  });
  const [create, setCreate] = React.useState(false);
  const [idToDelete, setIdToDelete] = React.useState(-1);
  const [idToUpdate, setIdToUpdate] = React.useState(-1);
  const [rulesModal, setRulesModal] = React.useState({
    open: false,
    user: null,
    rights: [],
    isSuperAdmin: false,
  });

  const columns = [
    { field: "id", headerName: "Id", width: 90, type: "number" },
    {
      field: "createdDate",
      headerName: t("account.form.createdDate"),
      width: 150,
      valueGetter: (params) => params.value.split("T")[0],
    },
    { field: "login", headerName: "Login", width: 150 },
    {
      field: "avatar",
      headerName: "Avatar VIP",
      width: 120,
      renderCell: (params) => (
        <LinkVipAvatar params={params.row} users={users} setUsers={setUsers} />
      ),
    },
    {
      field: "action",
      headerName: "Actions",
      width: 130,
      renderCell: (params) => (
        <Box>
          <IconButton
            variant="contained"
            color="primary"
            size="small"
            onClick={() => editUser(params.row.id)}
          >
            <Edit />
          </IconButton>
          <IconButton
            variant="contained"
            color="primary"
            size="small"
            onClick={() => confirmDelete(params.row.id)}
          >
            <Delete />
          </IconButton>
          {location.search.includes("mode=rights") && userSuperAdmin === 1 && (
            <IconButton
              variant="contained"
              color="primary"
              size="small"
              onClick={() => {
                setRulesModal({
                  ...rulesModal,
                  user: params.row,
                  isSuperAdmin: params.row.isSuperAdmin === 0 ? false : true,
                  rights: params.row.rights ? params.row.rights.split(",") : [],
                  open: true,
                });
              }}
            >
              <SettingsIcon />
            </IconButton>
          )}
        </Box>
      ),
    },
    { field: "email", headerName: "Email", width: 250 },
    {
      field: "role",
      headerName: "Role",
      width: 120,
      renderCell: (params) =>
        params.value === "USER"
          ? t("users.user").toUpperCase()
          : t("users.admin").toUpperCase(),
    },
    { field: "platform", headerName: t("users.platform"), width: 150 },
    { field: "locale", headerName: t("users.language"), width: 120 },
  ];

  const handlePageChange = (page) => {
    setPage(page + 1);
  };

  const handleSortModelChange = (params) => {
    if (params !== sortModel) {
      setSortModel(params[0]);
    }
  };

  useEffect(() => {
    loadData();
  }, [page, sortModel]);

  const loadData = () => {
    setLoading(true);
    if (location.search.includes("mode=rights") && userSuperAdmin === 1) {
      userService
        .getOnlyAdmins((page - 1) * 10, 10, {
          login,
          email,
          platform,
          sortModel,
        })
        .then((result) => {
          setRowCount(() => result.count);
          setUsers(() => result.result);
        })
        .finally(() => setLoading(false));
    } else {
      userService
        .getAll((page - 1) * 10, 10, { login, email, platform, sortModel })
        .then((result) => {
          setRowCount(() => result.count);
          setUsers(() => result.result);
        })
        .finally(() => setLoading(false));
    }
  };

  const search = () => {
    loadData();
  };

  const reinit = async () => {
    await setEmail(() => "");
    await setLogin(() => "");
    await setPlatform(() => "");
    loadData();
  };

  const creation = () => {
    setCreate(true);
  };

  const save = (data) => {
    if (data.id && data.id > 0) {
      userService
        .update(data)
        .then(() => {
          setIdToUpdate(-1);
          enqueueSnackbar(`${t("toasts.userUpdateSuccess")}`, {
            variant: "success",
          });
          loadData();
        })
        .catch((err) => {
          if (err === 403)
            enqueueSnackbar(t("toasts.unauthorizedError"), {
              variant: "error",
            });
          else enqueueSnackbar(t("toasts.unknownError"), { variant: "error" });
        });
    } else {
      userService
        .create(data)
        .then((result) => {
          setCreate(false);
          enqueueSnackbar(`${result.login} ${t("toasts.userCreateSuccess")}`, {
            variant: "success",
          });
          loadData();
        })
        .catch((err) => {
          if (err === 403)
            enqueueSnackbar(t("toasts.unauthorizedError"), {
              variant: "error",
            });
          else enqueueSnackbar(t("toasts.unknownError"), { variant: "error" });
        });
    }
  };

  const savePassword = (password, id) => {
    userService
      .savePassword(id, password)
      .then((result) => {
        enqueueSnackbar(t("toasts.updatePasswordFromAccount"), {
          variant: "success",
        });
      })
      .catch((err) => {
        if (err === 403)
          enqueueSnackbar(t("toasts.unauthorizedError"), {
            variant: "error",
          });
        else enqueueSnackbar(t("toasts.unknownError"), { variant: "error" });
      });
  };

  const _delete = () => {
    userService
      .delete(idToDelete)
      .then(() => {
        setCreate(false);
        enqueueSnackbar(
          `${users.find((u) => u.id === idToDelete).login} ${t(
            "toasts.userDeleteSuccess"
          )}`,
          { variant: "success" }
        );
        loadData();
      })
      .catch((err) => {
        if (err === 403)
          enqueueSnackbar(t("toasts.unauthorizedError"), {
            variant: "error",
          });
        else enqueueSnackbar(t("toasts.unknownError"), { variant: "error" });
      })
      .finally(() => setIdToDelete(-1));
  };

  const confirmDelete = (id) => {
    setIdToDelete(id);
  };

  const editUser = (id) => {
    setIdToUpdate(id);
  };

  const setOpen = (value) => {
    if (!value) setIdToDelete(-1);
  };

  const updateRights = () => {
    userService
      .updateUserRights(rulesModal.user.id, rulesModal.rights.join())
      .then(() => {
        if (myUserId === rulesModal.user.id)
          dispatch(updateUserRights(rulesModal.rights.join()));
        if (rulesModal.isSuperAdmin != rulesModal.user.isSuperAdmin) {
          userService
            .updateSuperAdmin(rulesModal.user.id, rulesModal.isSuperAdmin)
            .then(() => {
              if (myUserId === rulesModal.user.id)
                dispatch(updateUserSuperAdmin(rulesModal.isSuperAdmin));
            })
            .catch((err) => {
              if (err === 403)
                enqueueSnackbar(t("toasts.unauthorizedError"), {
                  variant: "error",
                });
              else
                enqueueSnackbar(t("toasts.updateUserRightError"), {
                  variant: "error",
                });
            });
        }
      })
      .catch((err) => {
        if (err === 403)
          enqueueSnackbar(t("toasts.unauthorizedError"), {
            variant: "error",
          });
        else
          enqueueSnackbar(t("toasts.updateUserRightError"), {
            variant: "error",
          });
      })
      .finally(() => {
        loadData();
        setRulesModal({
          open: false,
          user: null,
          rights: [],
          isSuperAdmin: false,
        });
        enqueueSnackbar(t("toasts.updateUserRightSuccess"), {
          variant: "success",
        });
      });
  };

  return (
    <StyledDiv>
      <Container className={classes.mobileMarginBottom}>
        <h1>{t("users.title")}</h1>
        <div>
          <h4 className={classes.marginBottom}>{t("users.filters")}</h4>
          <div className={classes.filter}>
            <TextField
              variant="outlined"
              margin="normal"
              id="login"
              label={t("account.form.username")}
              name="login"
              value={login}
              onChange={(e) => setLogin(e.target.value)}
            />
            <TextField
              variant="outlined"
              margin="normal"
              id="email"
              label={t("account.form.email")}
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <TextField
              variant="outlined"
              margin="normal"
              id="platform"
              label={t("users.platform")}
              name="platform"
              value={platform}
              onChange={(e) => setPlatform(e.target.value)}
            />
          </div>
          <div className={classes.filterButtons}>
            <Button onClick={search} variant="contained" color="primary">
              <Search />
              {t("users.buttons.search")}
            </Button>
            <Button
              onClick={reinit}
              variant="contained"
              style={{ marginLeft: "1rem" }}
            >
              {t("users.buttons.init")}
            </Button>
          </div>
          <div>
            <IconButton
              variant="contained"
              color="primary"
              onClick={creation}
              size="large"
            >
              <Add />
            </IconButton>
          </div>
        </div>
        <DataGrid
          rows={users}
          columns={columns}
          autoHeight
          disableRowSelectionOnClick
          pagination
          paginationMode="server"
          rowCount={rowCount}
          loading={loading}
          sortingMode="server"
          onSortModelChange={handleSortModelChange}
          onPaginationModelChange={(model, details) =>
            handlePageChange(model.page)
          }
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
          }}
          pageSizeOptions={[10]}
          columnVisibilityModel={{ avatar: userSuperAdmin === 1 }}
        />
        <ResponsiveDialogManager
          onClose={() => setCreate(false)}
          open={create}
          renderer={<UserForm save={save} cancel={() => setCreate(false)} />}
          title={t("users.buttons.creation")}
          toolbarActions={
            <ToolbarDefaultActions cancel={() => setCreate(false)} />
          }
        />
        <ResponsiveDialogManager
          onClose={() => setIdToUpdate(0)}
          open={idToUpdate > 0}
          renderer={
            <UserForm
              save={save}
              user={users.find((u) => u.id === idToUpdate)}
              savePassword={savePassword}
              cancel={() => setIdToUpdate(-1)}
            />
          }
          title={t("users.buttons.update")}
          toolbarActions={
            <ToolbarDefaultActions cancel={() => setIdToUpdate(-1)} />
          }
        />
        <ResponsiveDialogManager
          onClose={() =>
            setRulesModal({
              open: false,
              user: null,
              rights: [],
              isSuperAdmin: false,
            })
          }
          open={rulesModal.open}
          renderer={
            <UserRights
              user={rulesModal.user}
              rights={rulesModal.rights}
              rulesModal={rulesModal}
              setRulesModal={setRulesModal}
            />
          }
          title={t("users.buttons.update")}
          toolbarActions={
            <ToolbarDefaultActions
              cancel={() =>
                setRulesModal({
                  open: false,
                  user: null,
                  rights: [],
                  isSuperAdmin: false,
                })
              }
              save={() => updateRights()}
            />
          }
        />
        <ConfirmationDialog
          open={idToDelete > 0}
          onAgree={_delete}
          element={
            idToDelete > 0 ? users.find((u) => u.id === idToDelete) : null
          }
          setOpen={setOpen}
          mode="deleteUser"
        />
      </Container>
    </StyledDiv>
  );
};
